import React, { useImperativeHandle, useState } from 'react';

import { BookingInquiryCargosForm } from '@components/admin/booking/BookingInquiryCargosForm';
import { BookingInquiryContactForm } from '@components/admin/booking/BookingInquiryContactForm';
import { BookingInquiryCustomerNoForm } from '@components/admin/booking/BookingInquiryCustomerNoForm';
import { BookingInquiryMiscInfoForm } from '@components/admin/booking/BookingInquiryMiscInfoForm';
import { BookingInquirySpecialNoteForm } from '@components/admin/booking/BookingInquirySpecialNoteForm';
import { FormDialog } from '@components/admin/FormDialog';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { AdminInquiryDetail, BookingInquiryAdminGroupNames } from '@models/BookingInquiry';
import type { PreAlertDetails } from '@models/next/PreAlert';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';

const strings = createStrings({
  ja: {
    changeTitle: (title: string) => `${title}を変更`,
  },
});

const useStyles = makeMuiStyles((_theme, _common) => ({
  dialog: {
    maxWidth: 'initial',
    width: 960,
  },
}));

export interface BookingInquiryDialogsRef {
  onOpenForm: (name: BookingInquiryAdminGroupNames) => void;
}

export interface BookingInquiryDialogProps {
  adminInquiryDetail: AdminInquiryDetail;
  preAlertDetails: PreAlertDetails;
}

export const BookingInquiryDialogs = React.forwardRef<
  BookingInquiryDialogsRef,
  BookingInquiryDialogProps
>((props, ref) => {
  const { adminInquiryDetail, preAlertDetails } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [currentForm, setCurrentForm] = useState<BookingInquiryAdminGroupNames>();
  const [nextDisabled, setNextDisabled] = useState(false);
  const [actionsVisible, setActionsVisible] = useState(true);

  const [maxSteps, setMaxSteps] = useState(0);

  useImperativeHandle(ref, () => ({
    onOpenForm: (name) => {
      setCurrentForm(name);
      setActionsVisible(true);
      if (name === 'cargos') {
        setMaxSteps(3);
      } else {
        setMaxSteps(0);
      }
    },
  }));

  return (
    <FormDialog
      open={currentForm != null}
      actionsVisible={actionsVisible}
      title={
        currentForm ? strings.changeTitle(bookingInquiryStrings.adminGroupTitle(currentForm)) : ''
      }
      onClose={() => setCurrentForm(undefined)}
      submitTitle={formStrings.update}
      alignTitle="center"
      dialogProps={{
        classes: {
          paper: classes.dialog,
        },
      }}
      nextDisabled={nextDisabled}
      maxSteps={maxSteps}
    >
      {currentForm
        ? {
            shipper: () => (
              <BookingInquiryContactForm
                contactType="shipper"
                adminInquiryDetail={adminInquiryDetail}
              />
            ),
            receiver: () => (
              <BookingInquiryContactForm
                contactType="receiver"
                adminInquiryDetail={adminInquiryDetail}
              />
            ),
            consignee: () => (
              <BookingInquiryContactForm
                contactType="consignee"
                adminInquiryDetail={adminInquiryDetail}
              />
            ),
            customerNo: () => (
              <BookingInquiryCustomerNoForm adminInquiryDetail={adminInquiryDetail} />
            ),
            cargos: () => (
              <BookingInquiryCargosForm
                preAlertDetails={preAlertDetails}
                adminInquiryDetail={adminInquiryDetail}
                onChangeNextDisabled={setNextDisabled}
                onChangeHasPickups={(hasPickups) => {
                  if (hasPickups) {
                    setMaxSteps(2);
                  } else {
                    setMaxSteps(1);
                  }
                }}
                onChangeActionsVisible={setActionsVisible}
                onClose={() => setCurrentForm(undefined)}
              />
            ),
            miscInfo: () => <BookingInquiryMiscInfoForm adminInquiryDetail={adminInquiryDetail} />,
            specialNote: () => (
              <BookingInquirySpecialNoteForm adminInquiryDetail={adminInquiryDetail} />
            ),
            routePlan: () => null,
          }[currentForm]()
        : null}
    </FormDialog>
  );
});
